<template>
  <b-container fluid>
    <div v-if="loading && !project" class="my-3 text-center">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <div v-else-if="project" class="my-3">
      <b-row>
        <b-col cols="12" lg="3">
          <h3 class="text-muted">{{ $t('projects.project-details') }}</h3>
        </b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.project-id') }}
        </b-col>
        <b-col cols="12" lg="9">{{ project.name }}</b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.project-manager') }}
        </b-col>
        <b-col v-if="!canManageProjectOwner" cols="12" lg="9">
          <span
            v-if="project.project_managers && !project.project_managers.length"
          >
            {{ $t('projects.no-pm-assigned') }}
          </span>
          <template v-else>
            <span
              v-for="(project_manager, index) in project.project_managers"
              :key="project_manager.sfid || project_manager.id"
            >
              {{
                `${project_manager.name}${
                  index == project.project_managers.length - 1 ? '' : ', '
                }`
              }}
            </span>
          </template>
        </b-col>
        <b-col v-else cols="12" lg="3" class="pb-2">
          <project-manager-dropdown
            :values="assignedProjectManagers(project)"
            :projectId="project.id"
            :projectManagers="projectManagers"
            @add="addProjectManager"
            @remove="removeProjectManager"
          />
        </b-col>
      </b-row>

      <b-row class="my-2 px4">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.jobs') }}
        </b-col>
        <b-col cols="12" lg="12">
          <b-table
            v-if="project.jobs && project.jobs.length"
            center
            :items="project.jobs"
            :fields="jobFields"
            ref="jobsTable"
          >
            <template #cell(_brand_ids)="{ item }">
              {{ item.job.name }}

              <p v-if="shouldDisplayQuote(item)">
                <a href="#" @click.prevent="getQuotePath(item.job.sfid)">
                  {{ $t('view-quote') }}
                </a>
              </p>
            </template>
            <template #cell(job_types)="{ item }">
              <JobTypes :item="item" />
            </template>
            <template #cell(job._price)="{ item }">
              &euro; {{ item.job.price_selling_sum }}
            </template>
            <template #cell(customer._fullname)="{ item }">
              {{ item.job.customer }}
            </template>
            <template #cell(job.created_date)="{ item }">
              {{ item.job.created_date | moment('L') }}
            </template>

            <template #cell(job.status)="{ item }">
              {{ statusesById[item.job.status] }}
            </template>

            <template #cell(_actions)="{ item, toggleDetails }">
              <b-button class="mr-2" size="sm" @click="toggleDetails">
                {{ $t('projects.details') }}
              </b-button>

              <div
                class="my-3"
                v-if="
                  shouldShowProposalMenuItems && shouldDisplayBrandActions(item)
                "
              >
                <p>
                  <strong>{{ $t('quote.actions') }}</strong>
                  :
                </p>
                <b-button
                  variant="primary"
                  size="sm"
                  class="mr-1"
                  @click.stop="acceptQuote(item)"
                  :disabled="quoteSaving"
                >
                  <b-spinner small v-if="quoteSaving"></b-spinner>
                  {{ $t('quote.accept') }}
                </b-button>
                <b-button
                  variant="danger"
                  size="sm"
                  @click.stop="rejectQuote(item)"
                  :disabled="quoteSaving"
                >
                  <b-spinner small v-if="quoteSaving"></b-spinner>
                  {{ $t('quote.reject') }}
                </b-button>
              </div>

              <project-documents-dropdown
                :buttonText="'Docs'"
                :item="item"
                :accessToken="accessToken"
              />
            </template>
            <template #cell(_diary)="{ item }">
              <b-dropdown
                v-if="
                  getDiaryEntries(item.job.sfid) &&
                  getDiaryEntries(item.job.sfid).length
                "
                size="sm"
                right
                class="diary-dropdown"
                boundary="viewport"
                :ref="`diary-dropdown-${item.job.sfid}`"
                @hide="preventDropdownClose($event, item.job.sfid)"
                @hidden="onDropdownHidden(item.job.sfid)"
              >
                <template #button-content>
                  <span>{{ $t('jobs.table.heading.diary') }}</span>
                  <b-badge
                    variant="light"
                    class="ml-2 font-weight-bold diary-count-badge"
                  >
                    {{ getDiaryEntries(item.job.sfid).length }}
                  </b-badge>
                </template>
                <div
                  v-for="(diary, index) in getDiaryEntries(item.job.sfid)"
                  :key="`diary-item-${item.job.sfid}-${index}`"
                  class="diary-item-wrapper"
                >
                  <b-dropdown-item
                    class="diary-dropdown-item p-0"
                    @click.native.stop.prevent="
                      toggleDiaryDetails(
                        item.job.sfid,
                        index,
                        diary,
                        item.job.sfid,
                        $event,
                      )
                    "
                  >
                    <div
                      class="diary-entry-header px-3 py-2"
                      :class="{
                        'border-bottom': isDiaryExpanded(item.job.sfid, index),
                        'bg-light': isDiaryExpanded(item.job.sfid, index),
                      }"
                    >
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div class="flex-grow-1">
                          <div class="font-weight-bold text-dark">
                            <template
                              v-if="
                                diary.answers && diary.answers['report-date']
                              "
                            >
                              {{
                                $moment(diary.answers['report-date']).format(
                                  'L',
                                )
                              }}
                              <span
                                v-if="diary.answers['custom-title']"
                                class="font-weight-normal text-muted"
                              >
                                - {{ diary.answers['custom-title'] }}
                              </span>
                            </template>
                            <template
                              v-else-if="
                                diary.answers && diary.answers['custom-title']
                              "
                            >
                              {{ diary.answers['custom-title'] }}
                            </template>
                            <template
                              v-else-if="
                                diary.answers &&
                                Object.keys(diary.answers).length > 0
                              "
                            >
                              {{ Object.values(diary.answers)[0] }}
                            </template>
                            <template v-else-if="diary.created_at">
                              {{ $moment(diary.created_at).format('L') }}
                            </template>
                            <template v-else-if="diary.createdAt">
                              {{ $moment(diary.createdAt).format('L') }}
                            </template>
                            <template v-else>
                              Entry #{{ diary.id || index + 1 }}
                            </template>
                          </div>
                        </div>
                        <i
                          class="material-icons align-middle text-primary"
                          style="font-size: 20px"
                        >
                          {{
                            isDiaryExpanded(item.job.sfid, index)
                              ? 'keyboard_arrow_up'
                              : 'keyboard_arrow_down'
                          }}
                        </i>
                      </div>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="isDiaryExpanded(item.job.sfid, index)"
                    class="diary-dropdown-item diary-details-item p-0"
                    :class="{
                      'border-bottom':
                        index < getDiaryEntries(item.job.sfid).length - 1,
                    }"
                    @click.native.stop.prevent
                    @mousedown.native.stop.prevent
                  >
                    <div
                      class="px-3 py-3 bg-white diary-form-answers-container"
                      style="max-width: 100%; overflow-x: auto"
                      @click.stop="
                        handleFormAnswersClick(item.job.sfid, $event)
                      "
                      @mousedown.stop
                    >
                      <b-spinner
                        v-if="diary.loading"
                        small
                        class="mr-2"
                      ></b-spinner>
                      <div
                        v-else-if="diary.details && diary.details.length"
                        @click.stop="
                          handleFormAnswersClick(item.job.sfid, $event)
                        "
                        @mousedown.stop
                      >
                        <form-answers
                          :answers="diary.details"
                          :complete-order="item"
                          class="mt-2"
                        />
                      </div>
                      <div v-else-if="!diary.loading" class="text-muted small">
                        No additional details available
                      </div>
                    </div>
                  </b-dropdown-item>
                </div>
              </b-dropdown>
              <span v-else class="text-muted">-</span>
            </template>
            <template #row-details="{ item }">
              <job-details :item="item" @refresh="$refs.jobsTable.refresh()" />
            </template>
          </b-table>
          <span v-else>{{ $t('projects.no-jobs-assigned') }}</span>
        </b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.project-value') }}
        </b-col>
        <b-col cols="12" lg="9">
          {{
            project.price_selling_sum && `${project.price_selling_sum} &euro;`
          }}
        </b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.customer') }}
        </b-col>
        <b-col
          cols="12"
          lg="9"
          v-if="project.customers && project.customers.length"
        >
          <div
            v-for="(customer, index) in project.customers"
            :key="customer.id"
          >
            <div>
              {{
                `${customer.name}${
                  index == project.customers.length - 1 ? '' : ', '
                }`
              }}
            </div>
            <div>{{ getCustomerShippingAddress(customer) }}</div>
          </div>
        </b-col>
        <b-col cols="12" lg="9" v-else>N/A</b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.created-date') }}
        </b-col>
        <b-col cols="12" lg="9">
          {{
            project.jobs &&
            project.jobs[0] &&
            project.jobs[0].job.created_date | moment('L')
          }}
        </b-col>
      </b-row>

      <b-row class="my-2 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.documentation') }}
        </b-col>
        <b-col cols="12" lg="9">
          <p
            v-for="(value, propertyName) in project.documents"
            :key="propertyName"
          >
            <strong>{{ $t(`projects.image.${propertyName}`) }}:</strong>
            &nbsp;
            <project-documents-dropdown
              :documentType="propertyName"
              :buttonText="'Download'"
              :item="value"
              :accessToken="accessToken"
            />
          </p>
        </b-col>
      </b-row>

      <b-row v-if="showProjectNotifications" class="my-2 pt3 border-bottom">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.notifications') }}
        </b-col>
        <b-col cols="12" lg="9">
          <div class="custom-control custom-switch">
            <input
              type="checkbox"
              v-model="project.notifications"
              class="custom-control-input"
              id="customSwitches"
              @change="switchChangeHandler"
            />
            <label
              class="custom-control-label gid-notifications"
              for="customSwitches"
            >
              {{ project.notifications ? 'On' : 'Off' }}
            </label>
          </div>
        </b-col>
      </b-row>

      <b-row v-if="showHistory" class="my-2 pt3 border-bottom pb-2">
        <b-col cols="12" lg="3" class="text-muted">
          {{ $t('projects.table.heading.history') }}
        </b-col>
        <b-col cols="12" lg="9">
          <b-button @click.stop="$refs.logView.showLog(project.id)">
            {{ $t('projects.view-history') }}
          </b-button>
          <log-list
            ref="logView"
            :statuses="statuses"
            :context="'brand-project'"
            :logs="project.logs"
          />
        </b-col>
      </b-row>
    </div>

    <div v-else-if="!loading && project === null">
      <h1>{{ $t('projects.project-not-found') }}</h1>
    </div>
  </b-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { CondOperator, RequestQueryBuilder } from '@dataui/crud-request';
import LogList from '@gid/vue-common/components/LogList';
import JobDetails from '../views/JobDetails.vue';
import ProjectManagerDropdown from './ProjectManagerDropdown.vue';
import ProjectDocumentsDropdown from './ProjectDocumentsDropdown.vue';
import FormAnswers from '@gid/vue-common/components/inputs/FormAnswers.vue';
import { JobStatusEnum, ContactProfileEnum } from '@gid/models';
import JobTypes from './JobTypes.vue';
import ProjectManagerRoleMixin from '../mixins/ProjectManagerRoleMixin';

export default {
  mixins: [ProjectManagerRoleMixin],
  components: {
    'log-list': LogList,
    'job-details': JobDetails,
    'project-manager-dropdown': ProjectManagerDropdown,
    'project-documents-dropdown': ProjectDocumentsDropdown,
    'form-answers': FormAnswers,
    JobTypes,
  },
  props: {
    projectId: {
      type: String,
      default: null,
    },
    projectData: {
      type: Object,
      default: null,
    },
    showHistory: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      project: null,
      loading: true,
      statuses: [],
      projectManagers: [],
      diaryEntries: {},
      expandedDiaryEntries: {},
      preventCloseFlags: {},
      jobFields: [
        { label: this.$t('jobs.table.heading.id'), key: '_brand_ids' },
        {
          label: this.$t('jobs.table.heading.job-types'),
          key: 'job_types',
        },
        {
          label: this.$t('jobs.table.heading.order-value'),
          key: 'job._price',
        },
        {
          label: this.$t('jobs.table.heading.customer'),
          key: 'customer._fullname',
        },
        {
          label: this.$t('jobs.table.heading.created-date'),
          key: 'job.created_date',
          sortable: true,
          formatter: (value) => (value ? this.$moment(value).format('L') : '-'),
        },
        {
          label: this.$t('jobs.table.heading.appointment'),
          key: 'job',
          formatter: (value) =>
            value.appointment_start_timestamp ? this.getTimeRange(value) : '-',
        },
        { label: this.$t('jobs.table.heading.status'), key: 'job.status' },
        {
          label: this.$t('jobs.table.heading.documentation'),
          key: '_actions',
          tdClass: 'text-nowrap',
        },
        {
          label: this.$t('jobs.table.heading.diary'),
          key: '_diary',
          tdClass: 'text-nowrap',
        },
      ],
      quoteSaving: false,
    };
  },
  computed: {
    ...mapGetters([
      'contactProfiles',
      'locale',
      'access_token',
      'shouldShowProposalMenuItems',
    ]),
    accessToken() {
      return this.access_token;
    },
    getCustomerShippingAddress() {
      return (customer) => {
        return `${customer?.shipping_address?.postal_code} ${customer?.shipping_address?.city}`;
      };
    },
    assignedProjectManagers() {
      return (item) => {
        if (item && item.project_managers && item.project_managers.length) {
          return item.project_managers.map((entry) => ({
            value: entry.sfid || entry.id,
            text: `${entry.first_name || entry.firstname || ''} ${
              entry.last_name || entry.lastname || ''
            }`.trim(),
          }));
        }
        return [];
      };
    },
    statusesById() {
      return this.statuses.reduce((statuses, status) => {
        statuses[status.value] = status.text;
        return statuses;
      }, {});
    },
    shouldDisplayQuote() {
      return (item) => {
        const statusesArray = [
          JobStatusEnum.PROPOSAL_APPROVAL_PENDING,
          JobStatusEnum.ACCEPTED,
          JobStatusEnum.ON_HOLD,
          JobStatusEnum.APPOINTMENT_CONFIRMED,
          JobStatusEnum.APPROVAL_PENDING,
          JobStatusEnum.FINISHED,
          JobStatusEnum.OPEN,
          JobStatusEnum.INVOICE_SENT,
          JobStatusEnum.COMPLETED,
        ];
        if (
          statusesArray.includes(item.job.status) &&
          this.project.opportunity.brand_can_view_quote
        ) {
          return true;
        }
        return false;
      };
    },
    showProjectNotifications() {
      return this.contactProfiles.some((cp) =>
        [
          ContactProfileEnum.PROJECT_MANAGER_DEFAULT,
          ContactProfileEnum.PROJECT_MANAGER_GENERIC,
        ].includes(cp),
      );
    },
    shouldDisplayBrandActions() {
      return (item) => {
        return (
          item.job.status === JobStatusEnum.BRAND_PROPOSAL_APPROVAL_PENDING
        );
      };
    },
  },
  async created() {
    if (this.projectData) {
      this.project = this.projectData;
      this.loading = false;
    } else if (this.projectId) {
      await this.fetchProject();
    }

    if (this.project) {
      this.getNotificationsForProject();
      await this.fetchDiaryEntries();
    }

    if (this.canManageProjectOwner) {
      this.fetchProjectManagers();
    }

    axios.get(`/api/brand/orders/statuses`).then((response) => {
      this.statuses = response.data.map((status) => ({
        text: status.brand_name[this.locale],
        value: status.job_status,
      }));
    });

    this.$root.$on('bv::modal::hide', this.onModalHide);
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::hide', this.onModalHide);
  },
  methods: {
    async fetchProject() {
      try {
        const response = await axios.get(
          `/api/brand/projects/${this.projectId}`,
        );
        this.project = response.data;
        this.loading = false;
      } catch (e) {
        this.project = null;
        this.loading = false;
        throw new Error(e);
      }
    },
    async fetchProjectManagers() {
      const response = await axios.get(`/api/brand/project-managers`);
      this.projectManagers = response.data.map((entry) => {
        return {
          value: entry.sfid,
          text: `${entry.first_name} ${entry.last_name}`,
        };
      });
    },
    async addProjectManager(projectId, sfid) {
      try {
        await axios.put(`/api/brand/projects/add-project-owner`, {
          project_id: `${projectId}`,
          contact_sfid: sfid,
        });
        const pmInfo = this.projectManagers.find((pm) => pm.value === sfid);
        const [firstName, ...lastParts] = (pmInfo?.text || '').split(' ');
        const newPm = {
          sfid,
          first_name: firstName || '',
          last_name: lastParts.join(' ') || '',
        };
        if (this.project) {
          this.$set(this.project, 'project_managers', [
            ...(this.project.project_managers || []),
            newPm,
          ]);
        }
      } catch (e) {
        throw new Error(e);
      }
    },
    async removeProjectManager(projectId, sfid) {
      try {
        await axios.delete(`/api/brand/projects/remove-project-owner`, {
          data: {
            project_id: `${projectId}`,
            contact_sfid: sfid,
          },
        });
        if (this.project) {
          this.$set(
            this.project,
            'project_managers',
            (this.project.project_managers || []).filter(
              (pm) => pm.sfid !== sfid,
            ),
          );
        }
      } catch (e) {
        throw new Error(e);
      }
    },
    getTimeRange(job) {
      return (
        this.$moment(job.appointment_start_timestamp).format('L H:mm') +
        ' - ' +
        this.$moment(job.appointment_end_timestamp).format('H:mm')
      );
    },
    async switchChangeHandler() {
      try {
        await axios.put(
          `/api/brand/notifications/projects/${this.project.id}`,
          {
            notifies: this.project.notifications,
          },
        );
      } catch (e) {
        throw new Error(e);
      }
    },
    async getNotificationsForProject() {
      try {
        const response = await axios.get(
          `/api/brand/notifications/projects/${this.project.id}`,
        );
        this.project.notifications = response.data.notifies;
      } catch (e) {
        throw new Error(e);
      }
    },
    async getQuotePath(job_id) {
      const resp = await axios.get(`/api/brand/orders/${job_id}/quote`);
      const path = `/api/media/file/${resp.data}?jwt=${this.access_token}`;
      window.open(path, '_blank').focus();
    },
    async acceptQuote(item) {
      try {
        this.quoteSaving = true;
        const payload = {
          preview: false,
          new_status: item.partner?.id
            ? JobStatusEnum.ACCEPTED
            : JobStatusEnum.OPEN,
          job_id: item.job.sfid,
        };
        await axios.post(`/api/brand/orders/set-status`, payload);
        this.quoteSaving = false;
        await this.fetchProject();
      } catch (e) {
        this.quoteSaving = false;
        throw new Error(e.message);
      }
    },
    async rejectQuote(item) {
      try {
        this.quoteSaving = true;
        await axios.post(`/api/brand/orders/set-status`, {
          preview: false,
          new_status: JobStatusEnum.CANCELLED,
          job_id: item.job.sfid,
        });
        this.quoteSaving = false;
        await this.fetchProject();
      } catch (e) {
        this.quoteSaving = false;
        throw new Error(e.message);
      }
    },
    async fetchDiaryEntries() {
      if (!this.project.jobs || !this.project.jobs.length) {
        return;
      }

      const jobIds = this.project.jobs
        .map((item) => item.job?.sfid)
        .filter(Boolean);
      if (!jobIds.length) {
        return;
      }

      try {
        const diaryPromises = this.project.jobs.map(async (item) => {
          if (!item.job?.sfid) {
            return { jobSfid: item.job?.sfid, entries: [] };
          }

          const searchFilter = {
            'job.id': { [CondOperator.EQUALS]: item.job.sfid },
          };

          const query = RequestQueryBuilder.create()
            .search(searchFilter)
            .query();

          try {
            const { data } = await axios.get(
              `/data-api/entity/submission/diaries?${query}`,
            );

            const entries = data.map((s) => ({
              ...s,
              job: item.job,
              answers: s.answers.reduce((all, a) => {
                all[a.question.name] = a.answer;
                return all;
              }, {}),
            }));

            return { jobSfid: item.job.sfid, entries };
          } catch (error) {
            return { jobSfid: item.job.sfid, entries: [] };
          }
        });

        const results = await Promise.all(diaryPromises);
        results.forEach(({ jobSfid, entries }) => {
          if (jobSfid) {
            this.$set(this.diaryEntries, jobSfid, entries);
          }
        });
      } catch (error) {
        // Error fetching diary entries - silently fail
      }
    },
    getDiaryEntries(jobSfid) {
      return this.diaryEntries[jobSfid] || [];
    },
    isDiaryExpanded(jobSfid, index) {
      const key = `${jobSfid}-${index}`;
      return this.expandedDiaryEntries[key] || false;
    },
    async toggleDiaryDetails(jobSfid, index, diary, dropdownSfid, event) {
      this.$set(this.preventCloseFlags, dropdownSfid, true);
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      const key = `${jobSfid}-${index}`;
      const isExpanded = this.expandedDiaryEntries[key] || false;

      if (isExpanded) {
        this.$set(this.expandedDiaryEntries, key, false);
      } else {
        this.$set(this.expandedDiaryEntries, key, true);

        if (diary.details === undefined) {
          this.$set(diary, 'loading', true);
          this.$set(diary, 'details', null);

          try {
            const jobApiId = diary.job?.api_id || this.getJobApiId(jobSfid);
            if (jobApiId) {
              const response = await axios.get(
                `/order-intake-forms-api/form/answers/${jobApiId}?submission-id=${diary.id}`,
              );
              this.$set(diary, 'details', response.data);
            } else {
              this.$set(diary, 'details', null);
            }
          } catch (error) {
            console.error('Error fetching diary details:', error);
            this.$set(diary, 'details', null);
          } finally {
            this.$set(diary, 'loading', false);
          }
        }
      }

      this.$nextTick(() => {
        const dropdownRef = this.$refs[`diary-dropdown-${dropdownSfid}`];
        if (dropdownRef) {
          if (!dropdownRef.visible) {
            dropdownRef.show();
          }
        }
        setTimeout(() => {
          this.$set(this.preventCloseFlags, dropdownSfid, false);
        }, 100);
      });
    },
    getJobApiId(jobSfid) {
      const job = this.project.jobs?.find((item) => item.job?.sfid === jobSfid);
      return job?.job?.api_id;
    },
    preventDropdownClose(event, jobSfid) {
      if (this.preventCloseFlags[jobSfid]) {
        event.preventDefault();
        const dropdownRef = this.$refs[`diary-dropdown-${jobSfid}`];
        if (dropdownRef) {
          this.$nextTick(() => {
            dropdownRef.show();
          });
        }
        return false;
      }

      const target = event?.relatedTarget || event?.target || event?.srcElement;
      if (target) {
        const isDiaryItem = target.closest('.diary-dropdown-item');
        const isDiaryHeader = target.closest('.diary-dropdown-header');
        const isDiaryToggleButton = target.closest('.diary-dropdown > button');
        const isFormAnswersContainer = target.closest(
          '.diary-form-answers-container',
        );
        const isFormAnswersButton =
          target.closest('button[class*="outline-primary"]') ||
          target.closest('button[class*="btn"]') ||
          target.closest('a[href*="/api/media/file/"]') ||
          target.closest('a[href*="jwt"]') ||
          target.closest('.form-answers') ||
          target.closest('form-answers') ||
          target.tagName === 'BUTTON' ||
          target.tagName === 'A';

        if (
          (isDiaryItem && !isDiaryHeader && !isDiaryToggleButton) ||
          isFormAnswersContainer ||
          isFormAnswersButton
        ) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          const dropdownRef = this.$refs[`diary-dropdown-${jobSfid}`];
          if (dropdownRef) {
            this.$nextTick(() => {
              dropdownRef.show();
            });
          }
          return false;
        }
      }

      return true;
    },
    handleFormAnswersClick(jobSfid, event) {
      const target = event?.target || event?.srcElement;
      if (target) {
        const isDownloadLink =
          target.closest('a[href*="/api/media/file/"]') ||
          target.closest('a[href*="jwt"]') ||
          (target.tagName === 'A' &&
            target.href &&
            (target.href.includes('/api/media/file/') ||
              target.href.includes('jwt'))) ||
          target.closest('button[href*="/api/media/file/"]');

        if (isDownloadLink) {
          this.$set(this.preventCloseFlags, jobSfid, true);
          if (event) {
            event.stopPropagation();
          }
          setTimeout(() => {
            this.$set(this.preventCloseFlags, jobSfid, false);
          }, 500);
          return;
        }
      }

      const viewButton = target.closest('button[class*="outline-primary"]');
      const isViewButton =
        viewButton &&
        (viewButton.textContent?.includes('View') ||
          viewButton.querySelector('i.material-icons')?.textContent ===
            'visibility');

      if (isViewButton) {
        this.$set(this.preventCloseFlags, jobSfid, true);
        if (event) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }

        setTimeout(() => {
          const modalExists =
            document.querySelector('.modal.show') ||
            document.querySelector('.modal-backdrop');
          if (!modalExists) {
            setTimeout(() => {
              this.$set(this.preventCloseFlags, jobSfid, false);
            }, 500);
          } else {
            this.$set(this.preventCloseFlags, jobSfid, true);
          }
        }, 100);
        return;
      }

      this.$set(this.preventCloseFlags, jobSfid, true);
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }

      setTimeout(() => {
        this.$set(this.preventCloseFlags, jobSfid, false);
      }, 200);
    },
    onDropdownHidden(jobSfid) {
      if (this.preventCloseFlags[jobSfid]) {
        const dropdownRef = this.$refs[`diary-dropdown-${jobSfid}`];
        if (dropdownRef) {
          this.$nextTick(() => {
            dropdownRef.show();
          });
        }
      }
    },
    onModalHide(bvEvent, modalId) {
      Object.keys(this.preventCloseFlags).forEach((jobSfid) => {
        if (this.preventCloseFlags[jobSfid]) {
          this.$set(this.preventCloseFlags, jobSfid, true);

          const dropdownRef = this.$refs[`diary-dropdown-${jobSfid}`];
          if (dropdownRef) {
            this.$nextTick(() => {
              if (!dropdownRef.visible) {
                dropdownRef.show();
              }
            });
          }

          setTimeout(() => {
            this.$set(this.preventCloseFlags, jobSfid, false);
          }, 800);
        }
      });
    },
  },
};
</script>

<style scoped>
.gid-notifications {
  cursor: pointer;
}
</style>

<style>
.diary-dropdown.show .dropdown-menu,
.diary-dropdown .dropdown-menu.show,
.diary-dropdown .dropdown-menu {
  max-height: 450px !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  min-width: 450px !important;
  max-width: min(650px, calc(100vw - 40px)) !important;
  width: min(650px, calc(100vw - 40px)) !important;
  padding: 0 !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.25rem !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  contain: layout !important;
  box-sizing: border-box !important;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar {
  width: 8px !important;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1 !important;
  border-radius: 4px !important;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-thumb {
  background: #888 !important;
  border-radius: 4px !important;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #555 !important;
}

.diary-dropdown {
  min-width: 200px;
}

.diary-dropdown .diary-count-badge {
  background-color: rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
  font-size: 0.875rem !important;
  padding: 0.25em 0.5em !important;
  border-radius: 0.25rem !important;
}

.diary-item-wrapper {
  display: contents;
}

.diary-dropdown-header {
  min-width: 450px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
}

.diary-dropdown-item {
  min-width: 450px;
  max-width: 100% !important;
  width: 100% !important;
  white-space: normal;
  padding: 0 !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box !important;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar {
  width: 8px;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.diary-dropdown .dropdown-menu::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.diary-dropdown-item {
  max-width: 100%;
  overflow: hidden;
}

.diary-dropdown .dropdown-menu .dropdown-item {
  white-space: normal;
  word-wrap: break-word;
  max-width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: hidden !important;
}

.diary-entry-header:hover {
  background-color: #e9ecef !important;
}

.diary-entry-header {
  transition: background-color 0.2s ease;
  cursor: pointer;
  min-height: 60px;
  display: flex;
  align-items: center;
}

.diary-dropdown-item:not(.diary-dropdown-header):hover {
  background-color: transparent;
}

.diary-dropdown-item.disabled {
  opacity: 1;
  cursor: default;
}

.diary-dropdown-item:not(.diary-dropdown-header) {
  pointer-events: auto;
}

.diary-dropdown-item:not(.diary-dropdown-header) > * {
  pointer-events: auto;
}

.diary-dropdown-item form-answers,
.diary-dropdown-item .form-answers,
.diary-dropdown-item button[class*='outline-primary'],
.diary-dropdown-item a[href*='/api/media/file/'] {
  pointer-events: auto;
}

.diary-dropdown .dropdown-menu {
  pointer-events: auto;
  max-height: 450px !important;
  overflow-y: auto !important;
}

.diary-dropdown .dropdown-menu .dropdown-item {
  pointer-events: auto;
}

.diary-form-answers-container {
  max-width: 100% !important;
  width: 100% !important;
  box-sizing: border-box !important;
  overflow-x: auto !important;
  overflow-y: visible !important;
}

.diary-form-answers-container * {
  max-width: 100% !important;
  box-sizing: border-box !important;
}
</style>
